import {
  httpRequest,
  fetchMiddlewareRetry,
  FetchMiddleware,
  FetchParams,
  FetchRequest,
} from "./httpRequest";

let accessToken: string | null = null;

class FetchError extends Error {
  status: number;
  response: { data: unknown; status: number };

  constructor(status: number, data: unknown) {
    super(`Request failed with status ${status}`);
    this.name = "FetchError";
    this.status = status;
    this.response = { data, status };
  }
}

type AxiosLikeRequestConfig = {
  method?: string;
  url?: string;
  data?: unknown;
  headers?: Record<string, string>;
  params?: AxiosLikeRequestParams;
  responseType?: "json" | "blob" | "text";
  validateStatus?: (status: number) => boolean;
};

type AxiosLikeFetchResponse<T> = {
  data: T;
  status: number;
};

type AxiosLikeParamValue = string | number | boolean;
type AxiosLikeRequestParams = Record<string, AxiosLikeParamValue>;
type AxiosLikeMethodCallConfig = Omit<
  AxiosLikeRequestConfig,
  "method" | "url" | "data"
>;

function buildAxiosLikeHeaders(
  extra?: Record<string, string>,
  hasBody?: boolean,
): Headers {
  const headers = new Headers();
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  if (hasBody && !extra?.["Content-Type"]) {
    headers.set("Content-Type", "application/json");
  }
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      headers.set(key, value);
    }
  }
  return headers;
}

function appendAxiosLikeParams(
  url: string,
  params?: AxiosLikeRequestParams,
): string {
  if (!params) return url;
  const query = new URLSearchParams(
    Object.entries(params).map(([k, v]) => [k, String(v)]),
  ).toString();
  return `${url}?${query}`;
}

async function executeAxiosLikeRequest<T>(
  url: string,
  method: string,
  body?: unknown,
  headers?: Record<string, string>,
  params?: AxiosLikeRequestParams,
  responseType?: "json" | "blob" | "text",
  validateStatus?: (status: number) => boolean,
): Promise<AxiosLikeFetchResponse<T>> {
  const hasBody = body !== undefined;
  const init: RequestInit = {
    method: method.toUpperCase(),
    headers: buildAxiosLikeHeaders(headers, hasBody),
    ...(hasBody ? { body: JSON.stringify(body) } : {}),
  };

  url = prependApiOrigin(url);
  url = appendAxiosLikeParams(url, params);
  const response = await fetch(url, init);

  let data: unknown;
  if (response.status === 204) {
    data = null;
  } else if (responseType === "blob") {
    data = await response.blob();
  } else if (responseType === "text") {
    data = await response.text();
  } else {
    try {
      data = await response.json();
    } catch {
      data = null;
    }
  }

  const isSuccess = validateStatus
    ? validateStatus(response.status)
    : response.ok;

  if (!isSuccess) {
    throw new FetchError(response.status, data);
  }

  return { data: data as T, status: response.status };
}

async function executeAxiosLikeRequestWithRetry<T>(
  url: string,
  method: string,
  body?: unknown,
  headers?: Record<string, string>,
  params?: AxiosLikeRequestParams,
  responseType?: "json" | "blob" | "text",
  validateStatus?: (status: number) => boolean,
): Promise<AxiosLikeFetchResponse<T>> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= 3; attempt++) {
    try {
      return await executeAxiosLikeRequest<T>(
        url,
        method,
        body,
        headers,
        params,
        responseType,
        validateStatus,
      );
    } catch (error) {
      lastError = error;
      if (
        error instanceof FetchError &&
        (error.status === 401 || error.status === 502) &&
        attempt < 3
      ) {
        await new Promise<void>((resolve) =>
          setTimeout(resolve, 1000 * Math.pow(2, attempt)),
        );
        continue;
      }
      throw error;
    }
  }
  throw lastError;
}

function fetchApi(params: FetchParams) {
  const middlewares = [
    fetchMiddlewarePrependApiOrigin(),
    fetchMiddlewareAddAuthentication(),
    fetchMiddlewareRetry([401, 502]),
    fetchMiddlewareThrowOnError(),
  ];
  return httpRequest({ ...params, middlewares });
}

function fetchApiUnauthenticated(params: FetchParams) {
  const middlewares = [
    fetchMiddlewarePrependApiOrigin(),
    fetchMiddlewareRetry([502]),
    fetchMiddlewareThrowOnError(),
  ];
  return httpRequest({ ...params, middlewares });
}

function fetchMiddlewarePrependApiOrigin(): FetchMiddleware {
  return {
    async preFetch(request: FetchRequest) {
      request.url = prependApiOrigin(request.url);
    },
  };
}

function fetchMiddlewareAddAuthentication(): FetchMiddleware {
  return {
    async preFetch(request: FetchRequest) {
      const authHeaders = getAuthHeaders();
      for (const [key, value] of Object.entries(authHeaders)) {
        request.headers.set(key, value);
      }
    },
  };
}

function fetchMiddlewareThrowOnError(): FetchMiddleware {
  return {
    async postFetch(request: FetchRequest, response: Response) {
      if (!response.ok && !request.allowedStatuses.includes(response.status)) {
        const data = await readResponseData(response);
        throw new FetchError(response.status, data);
      }
    },
  };
}

async function readResponseData(response: Response) {
  const responseType = response.headers.get("Content-Type");
  let data: string | object | Blob | null;
  if (response.status === 204) {
    data = null;
  } else if (responseType?.startsWith("application/octet-stream")) {
    data = await response.blob();
  } else if (responseType?.startsWith("text/plain")) {
    data = await response.text();
  } else if (responseType?.startsWith("application/json")) {
    data = await response.json();
  } else {
    try {
      data = await response.json();
    } catch {
      data = null;
    }
  }
  return data;
}

/**
 * A simple wrapper around fetch that automatically includes the access token in the Authorization header.
 * Created as a direct swap out for our use of axios to minimize changes needed in the codebase.
 * @deprecated Use fetchApi() instead.
 */
const authenticatedAxios = {
  request<T = any>(
    config: AxiosLikeRequestConfig,
  ): Promise<AxiosLikeFetchResponse<T>> {
    const {
      method = "GET",
      url,
      data,
      headers,
      params,
      responseType,
      validateStatus,
    } = config;
    return executeAxiosLikeRequestWithRetry<T>(
      url,
      method,
      data,
      headers,
      params,
      responseType,
      validateStatus,
    );
  },

  get<T = any>(
    url: string,
    config?: AxiosLikeMethodCallConfig,
  ): Promise<AxiosLikeFetchResponse<T>> {
    return executeAxiosLikeRequestWithRetry<T>(
      url,
      "GET",
      undefined,
      config?.headers,
      config?.params,
      config?.responseType,
      config?.validateStatus,
    );
  },

  post<T = any>(
    url: string,
    data?: unknown,
    config?: AxiosLikeMethodCallConfig,
  ): Promise<AxiosLikeFetchResponse<T>> {
    return executeAxiosLikeRequestWithRetry<T>(
      url,
      "POST",
      data,
      config?.headers,
      config?.params,
      config?.responseType,
      config?.validateStatus,
    );
  },

  patch<T = any>(
    url: string,
    data?: unknown,
    config?: AxiosLikeMethodCallConfig,
  ): Promise<AxiosLikeFetchResponse<T>> {
    return executeAxiosLikeRequestWithRetry<T>(
      url,
      "PATCH",
      data,
      config?.headers,
      config?.params,
      config?.responseType,
      config?.validateStatus,
    );
  },

  delete<T = any>(
    url: string,
    config?: AxiosLikeMethodCallConfig,
  ): Promise<AxiosLikeFetchResponse<T>> {
    return executeAxiosLikeRequestWithRetry<T>(
      url,
      "DELETE",
      undefined,
      config?.headers,
      config?.params,
      config?.responseType,
      config?.validateStatus,
    );
  },
};

const fetcher = <Data = unknown>(url: string): Promise<Data> =>
  fetchApi({ url, method: "GET" }).then((response) =>
    response.status === 204 ? null : (response.json() as Promise<Data>),
  );

const setFetcherAccessToken = (token: string): void => {
  accessToken = token;
};

function prependApiOrigin(url: string) {
  const origin =
    globalThis.__ENV?.RUNTIME_PUBLIC_API_ORIGIN ||
    process.env.RUNTIME_PUBLIC_API_ORIGIN;
  if (!origin || !url?.startsWith("/")) return url;
  return new URL(url, origin).toString();
}

function getAuthHeaders(): Record<string, string> {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

export {
  authenticatedAxios,
  fetcher,
  setFetcherAccessToken,
  fetchApi,
  fetchApiUnauthenticated,
  prependApiOrigin,
  FetchError,
};
